import React, { useMemo, useState } from 'react';
import ProjectSummary from './ProjectSummary';
import ProjectBudget from './ProjectBudget';
import ProjectTable from './ProjectTable';
import ProjectPreviousFundingsTable from './ProjectPreviousFundingsTable';
import { Collapse, Fade, Grid, LinearProgress, Skeleton, Stack, Tooltip } from '@mui/material';
import { getBudgetandLineItems, getProjectDraws, exportProjectToExcel } from './project.api';
import { deleteProject } from '../../components/deleteItem/deleteItem.api';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { handleError } from '../../common/utils/util.errors';
import ProjectFooter from './ProjectFooter';
import { ROUTES } from '../../common/constants/urls';
import { toast } from 'react-toastify';
import keys from '../../common/react-query/data-keys';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Assessment, Delete, Edit, Launch, Visibility, VisibilityOff } from '@mui/icons-material';
import { Typography, Box, Button } from 'components/index';
import { deepCopy, joinDateTimeAndUser } from 'common/utils/common.util';
import { usePrompt } from '../../components/AppPrompt';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import TabPanel from '../../components/general/TabPanel';
import {
  BudgetSummaryDTO,
  DrawSummary,
  FileUploadErrorDTO,
  ProjectData,
  ProjectLineItemsAndSummary,
  ProjectSummary as ProjectSummaryType,
} from './project.type';
import ProjectDrawSummary from './ProjectDrawSummary';
import { useAppContext } from 'common/AppContext';
import { getLinkedReports } from './project.api';
import { isValidGeneralInfo } from '../../components/report/report.util';
import { generateReport } from '../../components/report/report.api';
import { ReportGeneralInfo } from '../../components/report/report.type';
import EditProjectDrawDialog from './project-draws/EditProjectDrawDialog';
import { CreateOrUpdateProjectDrawDTO } from './project-draws/project-draws.type';
import { setProjectDrawVisibility } from './project-draws/project-draws.api';
import WhiteFab from '../common/WhiteFab';
import { NoteDTO } from 'common/utils/util.type';
import { getDivisions } from '../../components/glossary/glossary.api';
import useAutoDivisionMapper from '../../components/glossary/useAutoDivisionMapper';
import { PredictedLineItemWithDivision } from '../../components/glossary/glossary.type';
import AutoAssignDivisionsDialog from '../../components/report/AutoAssignDivisionsDialog';
import { getErrorFieldNames, validateDrawUniqueness, sumPreviousFundings } from './project.util';
import ProjectMap from '../map/ProjectMap';
import ProjectMiscTabs from '../general/ProjectMiscTabs';
import { ONE_DAY_MS } from '../../common/constants/general';
import {
  createLimitedReport,
  getLimitedReportsByProject,
  deleteLimitedReport,
} from '../monitoring-reports/limited-report/limited-report.api';
import { PROJECT_TABLE_SCROLL_HEIGHT, isMagicReportAwaitingPayapp } from './project.util';
import { useMagicReportDrawSelection } from '../../hooks/useMagicReportDrawSelection';
import MagicReportDrawSelectDialog from './MagicReportDrawSelectDialog';
import RequiredFieldsGateDialog from './RequiredFieldsGateDialog';

// Tab order must only depend on fields the draws list endpoint returns, so it never shifts when a draw
// is hidden/unhidden or when a draw's details (e.g. applicationNumber) load lazily on click.
const periodStartTime = (draw: ProjectLineItemsAndSummary) => {
  const time = Date.parse(draw.projectDrawSummary?.periodBeginning || '');
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
};
const compareDrawsForTabs = (a: ProjectLineItemsAndSummary, b: ProjectLineItemsAndSummary) =>
  periodStartTime(a) - periodStartTime(b) || (a.projectDrawSummary?.name || '').localeCompare(b.projectDrawSummary?.name || '');

interface ProjectDetailsProps {
  projectId?: string;
  project: ProjectData;
  projectSummary?: ProjectSummaryType;
  isLoadingProjectSummary: boolean;
  isErrorProjectSummary: boolean;
  errorProjectSummary: any;
  isSaving: boolean;
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  onEditChange: (field: string, value: any) => void;
  onSave: (payappDrawId?: string) => void;
  onCancel: () => void;
  projectErrors: FileUploadErrorDTO[];
  isProjectDirty: boolean;
  updateProject: (updatedProject: Partial<ProjectData>, isEditMode: boolean, updateInitialProject?: boolean) => void;
  updateInitialProject: (updatedDraw: ProjectLineItemsAndSummary) => void;
  readonly?: boolean;
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({
  projectId,
  project,
  projectSummary,
  isLoadingProjectSummary,
  isErrorProjectSummary,
  errorProjectSummary,
  isSaving,
  isEditMode,
  setIsEditMode,
  onEditChange,
  onSave,
  onCancel,
  projectErrors,
  isProjectDirty,
  updateProject,
  updateInitialProject,
  readonly,
}) => {
  const [deleting, setDeleting] = React.useState(false);
  const [notes, setNotes] = useState<NoteDTO[]>([]);
  const [shouldRefetchNotes, setShouldRefetchNotes] = useState(false);
  const { getConfirmation } = usePrompt();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setPageHeaderText } = useAppContext();
  const [selectedDrawId, setSelectedDrawId] = useState<string | undefined>(undefined);
  const lastSelectedDrawNameRef = React.useRef<string | undefined>(undefined);
  const [editDrawDialogOpen, setEditDrawDialogOpen] = useState(false);
  const [editDrawDialogValues, setEditDrawDialogValues] = useState<CreateOrUpdateProjectDrawDTO | undefined>(undefined);
  const [editDrawInProgress, setEditDrawInProgress] = useState(false);
  const [deleteDrawInProgress, setDeleteDrawInProgress] = useState(false);
  const [visibilityChangeInProgress, setVisibilityChangeInProgress] = useState(false);
  const [hasCalledHandlePayappSave, setHasCalledHandlePayappSave] = React.useState(false);
  const {
    openDrawSelection,
    confirmDrawSelection,
    closeDialog: closeMagicReportDrawSelect,
    dialogOpen: magicReportDrawSelectOpen,
    draws: magicReportDraws,
    project: magicReportProject,
    existingDrawIds: magicReportExistingDrawIds,
    existingHiddenCoLienDrawIds: magicReportExistingHiddenCoLienDrawIds,
    hasExistingReport: hasExistingMagicReport,
    isOpening: magicReportOpening,
    mode: magicReportMode,
    requiredFieldsGap,
    requiredFieldsGapProjectId,
    requiredFieldsGapProjectSummary,
    requiredFieldsGapOpen,
    retryAfterFieldsFixed,
    closeRequiredFieldsGap,
  } = useMagicReportDrawSelection();
  const [isPayappAutoSaving, setIsPayappAutoSaving] = React.useState(!!((location.state || {}) as any).payappMergedDraw);

  const { payappMergedDraw, payappDrawId } = (location.state || {}) as {
    payappMergedDraw?: ProjectLineItemsAndSummary;
    payappDrawId: string;
  };

  React.useEffect(() => {
    if (payappMergedDraw) {
      // Automatically clear location state after first use
      window.history.replaceState({}, document.title);
    }
  }, []);

  const [detailedDraws, setDetailedDraws] = useState<ProjectLineItemsAndSummary[]>(deepCopy(project.projectDraws));

  React.useEffect(() => {
    setDetailedDraws(deepCopy(project.projectDraws));
  }, [project.projectDraws]);

  const [generateReportInProgress, setGenerateReportInProgress] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { data: divisions } = useQuery(
    keys.divisions,
    async () => {
      return await getDivisions();
    },
    { staleTime: ONE_DAY_MS },
  );

  const { openAutoAssignDialog, isAutoAssignDialogOpen, lineItemsToAutoAssign, predictingDivisions, setIsAutoAssignDialogOpen } =
    useAutoDivisionMapper(divisions || []);

  const filteredDraws = useMemo(() => detailedDraws.filter(draw => !draw.isDeleted).sort(compareDrawsForTabs), [detailedDraws]);

  // The active tab is tracked BY ID, never by position, so hiding/unhiding or lazily loaded details can't
  // swap which draw is shown. While the payapp autosave is pending, it stays pinned to the payapp draw so
  // ProjectTable's payapp merge effect runs against the right draw. Fallbacks: by name (a saved temp draw
  // comes back with a real id), then the first visible draw.
  const activeDrawId = payappDrawId && !hasCalledHandlePayappSave ? payappDrawId : selectedDrawId;
  const selectedDraw =
    filteredDraws.find(draw => draw.projectDrawSummary.id === activeDrawId) ??
    filteredDraws.find(draw => !!lastSelectedDrawNameRef.current && draw.projectDrawSummary.name === lastSelectedDrawNameRef.current) ??
    filteredDraws.find(draw => draw.projectDrawSummary.isEnabled !== false) ??
    filteredDraws[0];
  const selectedDrawKey = selectedDraw?.projectDrawSummary.id;

  React.useEffect(() => {
    lastSelectedDrawNameRef.current = selectedDraw?.projectDrawSummary.name;
    if (selectedDrawKey && selectedDrawKey !== selectedDrawId) {
      setSelectedDrawId(selectedDrawKey);
    }
  }, [selectedDrawKey, selectedDrawId, selectedDraw?.projectDrawSummary.name]);

  const handleDrawEditChange = (
    fieldOrEdits: string | Array<{ field: string; value: any; target: 'drawSummary' | 'budgetSummary' }>,
    value?: any,
    target?: 'drawSummary' | 'budgetSummary',
  ) => {
    if (!currentDraw) return;
    const currentDrawId = currentDraw.projectDrawSummary.id;
    const edits = Array.isArray(fieldOrEdits) ? fieldOrEdits : [{ field: fieldOrEdits, value, target: target! }];

    const updatedDetailedDraws = detailedDraws.map(draw => {
      if (draw.projectDrawSummary.id === currentDrawId) {
        let updatedDraw = { ...draw };
        edits.forEach(edit => {
          if (edit.target === 'drawSummary') {
            const updatedDrawSummary = { ...updatedDraw.projectDrawSummary, [edit.field]: edit.value };
            updatedDraw = { ...updatedDraw, projectDrawSummary: updatedDrawSummary };
          } else if (edit.target === 'budgetSummary') {
            // Ensure budgetSummary exists before updating
            const currentBudgetSummary = updatedDraw.budgetSummary || ({} as BudgetSummaryDTO);
            const updatedBudgetSummary = { ...currentBudgetSummary, [edit.field]: edit.value };
            updatedDraw = { ...updatedDraw, budgetSummary: updatedBudgetSummary };
          }
        });
        return { ...updatedDraw, isEdited: !draw.isCreated };
      }
      return draw;
    });

    setDetailedDraws(updatedDetailedDraws);
    updateProject({ projectDraws: updatedDetailedDraws }, true);
  };

  const handleCreateLimitedReport = () => {
    if (!projectSummary || !currentDraw) {
      toast.error('Cannot create report without project and G702/3 information.');
      return;
    }

    const reportData = {
      projectSummary: projectSummary,
      drawSummary: currentDraw.projectDrawSummary,
      budgetSummary: currentBudgetSummary,
      lineItems: currentDraw.lineItems,
      subtotals: currentDraw.subtotals,
      summary: {
        coverPageContent: undefined,
      },
    };

    navigate(`${ROUTES.PROJECTS}/${projectId}${ROUTES.LIMITED_REPORT}/new/upload-images`, { state: { reportData } });
  };

  const handleOpenExistingLimitedReport = async (draw?: ProjectLineItemsAndSummary, budgetSummary?: BudgetSummaryDTO) => {
    if (!projectSummary) {
      toast.error('Cannot open report without project information.');
      return;
    }

    try {
      let report;
      try {
        const reports = await getLimitedReportsByProject(projectSummary.projectId);
        report = reports.length > 0 ? reports[0] : undefined;
      } catch (error) {
        report = undefined;
      }

      const reportData = {
        report: report,
        projectSummary: projectSummary,
        drawSummary: draw?.projectDrawSummary,
        budgetSummary: budgetSummary,
        lineItems: draw?.lineItems,
        subtotals: draw?.subtotals,
        summary: {
          coverPageContent: report?.summary?.coverLetter,
        },
      };
      navigate(`${ROUTES.PROJECTS}/${projectSummary.projectId}${ROUTES.LIMITED_REPORT}/${report?.id || 'new'}/upload-images`, {
        state: { reportData },
      });
    } catch (error) {
      handleError(error);
      return;
    }
  };

  const handleCreateNewLimitedReportForDraw = async (draw?: ProjectLineItemsAndSummary, budgetSummary?: BudgetSummaryDTO) => {
    if (!projectSummary) {
      toast.error('Cannot create report without project information.');
      return;
    }

    try {
      // Check if a report exists
      const reports = await getLimitedReportsByProject(projectSummary.projectId);
      const existingReport = reports.length > 0 ? reports[0] : undefined;

      if (existingReport) {
        // Show confirmation dialog
        const isConfirmed = await getConfirmation({
          heading: 'Create New Report',
          description: 'Starting a new report will archive the current one. Continue?',
          confirmButton: {
            label: 'Continue',
            color: 'error',
          },
          cancelButton: {
            label: 'Cancel',
            color: 'secondary',
          },
        });

        if (!isConfirmed) return;

        // Delete existing report
        await deleteLimitedReport(existingReport.id);
      }

      // Create new report
      const reportName = draw
        ? `Limited Report: ${projectSummary.projectTitle} - ${draw.projectDrawSummary.name}`
        : `Limited Report: ${projectSummary.projectTitle}`;

      const report = await createLimitedReport({
        name: reportName,
        projectId: projectSummary.projectId,
      });

      if (!report) {
        toast.error('Failed to create report.');
        return;
      }

      const reportData = {
        report: report,
        projectSummary: projectSummary,
        drawSummary: draw?.projectDrawSummary,
        budgetSummary: budgetSummary,
        lineItems: draw?.lineItems,
        subtotals: draw?.subtotals,
        summary: {
          coverPageContent: undefined,
        },
      };
      navigate(`${ROUTES.PROJECTS}/${projectSummary.projectId}${ROUTES.LIMITED_REPORT}/${report.id}/upload-images`, {
        state: { reportData },
      });
    } catch (error) {
      handleError(error);
      return;
    }
  };

  // Opening a magic report always goes through the draw-selection dialog so the user can see and
  // change which draws it covers.
  const handleOpenExistingMagicReport = async () => {
    if (!projectSummary) {
      toast.error('Cannot open report without project information.');
      return;
    }
    await openDrawSelection(projectSummary.projectId, 'open', { onDrawsHidden: handleMagicReportDrawsHidden });
  };

  const handleMagicReportDrawsHidden = (drawIds: string[]) => applyDrawVisibility(drawIds, false);

  const magicReportAwaitingPayapp = isMagicReportAwaitingPayapp(projectSummary);

  const handleNextMagicReportStarted = (reportId: string) => {
    if (!projectSummary) return;
    updateProject({ projectSummary: { ...projectSummary, magicReportId: reportId, magicReportPayappUploadedDrawIds: [] } }, false, true);
  };

  const handleMagicReportDrawConfirm = async (drawIds: string[], hiddenCoLienDrawIds: string[]) => {
    await confirmDrawSelection(drawIds, hiddenCoLienDrawIds, {
      onConfirmReplace: () =>
        getConfirmation({
          heading: 'Change report draws',
          description:
            'Changing the selected draws will archive the current magic report and start a new one for the newly selected draws. Continue?',
          confirmButton: { label: 'Continue', color: 'error' },
          cancelButton: { label: 'Cancel', color: 'secondary' },
        }),
    });
  };

  const handleCreateMagicReportForDraw = async () => {
    if (!projectSummary) {
      toast.error('Cannot create report without project information.');
      return;
    }
    await openDrawSelection(projectSummary.projectId, 'next-report', { onNextReportStarted: handleNextMagicReportStarted });
  };

  // Function to handle tab change
  const handleTabChange = (event: any, newValue: string) => {
    // Don't switch tabs mid hide/unhide — the toggle acts on the selected draw.
    if (visibilityChangeInProgress) return;
    setSelectedDrawId(newValue);
  };

  // Fetch project draws
  const {
    data: projectDrawData,
    isLoading: isLoadingDraws,
    isError: isErrorDraws,
    error: errorInDraws,
  } = useQuery([keys.projectDraws, projectId], () => getProjectDraws(projectId ?? ''), {
    enabled: !readonly && !!projectId,
    onSuccess: data => {
      const updatedProjectDraws = data.map(draw => ({
        projectDrawSummary: {
          id: draw.id,
          name: draw.name,
          applicationNumber: '',
          periodBeginning: draw.periodBeginning || '',
          periodEnding: draw.periodEnding || '',
          isEnabled: draw.isEnabled,
        },
      }));
      updateProject({ projectDraws: updatedProjectDraws }, false, true);
      setDetailedDraws(updatedProjectDraws);
    },
    onError: error => {
      handleError(error);
    },
  });

  const {
    data: associatedReports,
    isLoading: isLoadingReports,
    isError: isErrorReports,
    error: errorReports,
  } = useQuery([keys.associatedReports, projectId], () => getLinkedReports(projectId), {
    enabled: !!projectId && !readonly,
  });
  if (isErrorReports) {
    console.log(errorReports);
  }

  const {
    data: drawData,
    isLoading: isLoadingCurrentDraw,
    isError: isErrorCurrentDraw,
    error: errorInCurrentDraw,
  } = useQuery(
    [keys.projectLineItemsAndBudgets, projectId, selectedDrawKey],
    () => getBudgetandLineItems(projectId ?? '', selectedDrawKey ?? ''),
    {
      enabled:
        !readonly &&
        !!projectId &&
        !!selectedDrawKey &&
        !selectedDrawKey.startsWith('temp-') &&
        !selectedDraw?.projectDrawSummary.applicationNumber,
      onSuccess: fetchedDraw => {
        // Update the local detailedDraws state
        setDetailedDraws(prev => {
          return prev.map(draw => (draw.projectDrawSummary?.id === fetchedDraw.projectDrawSummary?.id ? fetchedDraw : draw));
        });

        // Update the global project state
        const updatedProjectDraws = project.projectDraws.map(draw =>
          draw.projectDrawSummary?.id === fetchedDraw.projectDrawSummary?.id ? fetchedDraw : draw,
        );

        updateProject({ projectDraws: updatedProjectDraws }, false, false);
        updateInitialProject(fetchedDraw);
      },
      onError: error => {
        // A 404 here means the draw has no payapp/summary yet — this is a valid empty draft state, not a user-facing error.
        const message = error instanceof Error ? error.message.toLowerCase() : '';
        if (!message.includes('not found')) {
          handleError(error);
        }
      },
      retry: false,
    },
  );
  const currentDraw = selectedDraw || drawData;

  const currentDrawIsEnabled = currentDraw?.projectDrawSummary?.isEnabled !== false;

  // "Less Previous Fundings" is derived live from the sum of "Funded by Lender" across the current
  // draw's Previous Fundings table, rather than being a manually-entered budget value.
  const previousFundingsTotal = React.useMemo(
    () => sumPreviousFundings(currentDraw?.projectDrawSummary?.previousFundings, 'amountFunded'),
    [currentDraw?.projectDrawSummary?.previousFundings],
  );

  const activeDrawLineItems = React.useMemo(() => (currentDraw?.lineItems || []).filter(li => !li.isDeleted), [currentDraw?.lineItems]);
  const totalDrawLineItemsCount = activeDrawLineItems.length;
  const assignedDrawLineItemsCount = activeDrawLineItems.filter(li => li.lineItem?.division != null).length;

  // Set the page header text (number before title, consistent from any entry point)
  React.useEffect(() => {
    const projectText =
      projectSummary?.projectNumber?.trim() && projectSummary?.projectTitle
        ? `${projectSummary.projectNumber} ${projectSummary.projectTitle}`
        : (projectSummary?.projectTitle ?? '');
    setPageHeaderText(projectText);
    return () => {
      setPageHeaderText('');
    };
  }, [projectSummary?.projectNumber, projectSummary?.projectTitle, setPageHeaderText]);

  const updateDivisions = async (updatedLineItems: PredictedLineItemWithDivision[]) => {
    try {
      setIsLoading(true);

      const updatedDetailedDraws = detailedDraws.map(draw => {
        // Check if the current draw contains line items to be updated
        const lineItemsToUpdate = updatedLineItems.filter(item => draw.lineItems?.some(lineItem => lineItem.id === item.id));

        if (lineItemsToUpdate.length > 0) {
          return {
            ...draw,
            lineItems: draw.lineItems?.map(lineItem => {
              const matchingItem = lineItemsToUpdate.find(item => item.id === lineItem.id);

              return matchingItem
                ? {
                    ...lineItem,
                    lineItem: {
                      ...lineItem.lineItem,
                      division: matchingItem.division || undefined,
                    },
                    isDeleted: false,
                    isCreated: lineItem.isCreated ? true : false,
                    isEdited: !lineItem.isCreated,
                  }
                : lineItem;
            }),
          };
        }

        return draw;
      });

      setDetailedDraws(updatedDetailedDraws as ProjectLineItemsAndSummary[]);

      // Update global state
      updateProject(
        {
          projectDraws: updatedDetailedDraws as ProjectLineItemsAndSummary[],
        },
        true,
      );
    } catch (error) {
      toast.error('Error updating divisions');
    } finally {
      setIsLoading(false);
    }
  };

  // calculate the budget summary
  const currentBudgetSummary = React.useMemo(() => {
    if (!currentDraw) return undefined;
    const lineItems = currentDraw.lineItems?.filter(li => !li.isDeleted);
    const projectBudget = currentDraw.budgetSummary;

    // Aggregate the required values
    const originalContractBudgetSum = Number(
      (lineItems?.reduce((sum, li) => sum + Number(Number(li.originalBudget || 0).toFixed(2)), 0) ?? 0).toFixed(2),
    );
    const reallocsChangeOrdersSum = Number(
      (
        lineItems?.reduce(
          (sum, li) => sum + Number(Number(li.reallocation || 0).toFixed(2)) + Number(Number(li.changeOrder || 0).toFixed(2)),
          0,
        ) ?? 0
      ).toFixed(2),
    );
    const contractBudgetSumToDate = Number((originalContractBudgetSum + reallocsChangeOrdersSum).toFixed(2));
    const totalCompletedAndStored = Number(
      (
        lineItems?.reduce(
          (sum, li) =>
            sum + Number(Number(Number(li.previousApplication || 0).toFixed(2)) + Number(Number(li.cdccApproved || 0).toFixed(2))),
          0,
        ) ?? 0
      ).toFixed(2),
    );
    const totalRetainageHeld = -Number((lineItems?.reduce((sum, li) => sum + Number(li.retainage || 0), 0) ?? 0).toFixed(2)); // Note: Negative of the total
    const totalRetainageHeldPercentage = totalCompletedAndStored > 0 ? (-totalRetainageHeld / totalCompletedAndStored) * 100 : 0;
    const percentageCompleteSum = contractBudgetSumToDate === 0 ? 0 : totalCompletedAndStored / contractBudgetSumToDate;

    const balanceToCompleteSum = Number((contractBudgetSumToDate - totalCompletedAndStored).toFixed(2));

    // Calculate derived values
    const totalEarnedLessRetainage = Number((totalCompletedAndStored + totalRetainageHeld).toFixed(2));
    const lessPrevFunding = Number(Number(projectBudget?.lessPreviousFundings || 0).toFixed(2));
    const currentPaymentDue = Number((totalEarnedLessRetainage - lessPrevFunding).toFixed(2));
    const balanceToCompletePlusRetainage = Number((balanceToCompleteSum - totalRetainageHeld).toFixed(2));
    const adjustmentsByCDCC = Number(Number(projectBudget?.adjustmentsByCDCC || 0).toFixed(2));

    const totalAmountReccForFunding = Number((adjustmentsByCDCC + currentPaymentDue).toFixed(2));
    const RequestedAmount = Number(Number(projectBudget?.requestedAmount || 0).toFixed(2));
    const difference = Number((totalAmountReccForFunding - RequestedAmount).toFixed(2));
    const estRetainageReleasedThisPeriod = Number(Number(projectBudget?.estRetainageReleasedThisPeriod || 0).toFixed(2));
    const totalContractBudgetPerComplete =
      contractBudgetSumToDate === 0 ? 0 : Number(((totalCompletedAndStored / contractBudgetSumToDate) * 100).toFixed(2));

    const budgetSummary: BudgetSummaryDTO = {
      originalContractBudgetSum,
      reallocationsChangeOrders: reallocsChangeOrdersSum,
      contractBudgetSumToDate,
      totalCompletedAndStored,
      totalRetainageHeld,
      totalRetainageHeldPercentage,
      totalEarnedLessRetainage,
      lessPreviousFundings: lessPrevFunding,
      currentPaymentDue,
      balanceToCompletePlusRetainage,
      adjustmentsByCDCC,
      totalAmountReccForFunding,
      requestedAmount: RequestedAmount,
      difference,
      estRetainageReleasedThisPeriod,
      totalContractBudgetPerComplete,
    };

    return budgetSummary;
  }, [currentDraw]);

  if (!projectId) {
    handleError('No Project to display!');
    return null;
  }

  const onDelete = async () => {
    if (!projectSummary) {
      toast.error('No Project to Delete!');
      return;
    }
    if (associatedReports && associatedReports.length > 0) {
      toast.error('Cannot delete this project as it has linked reports.');
      return;
    }
    const confirmed = await getConfirmation({
      description: `Are you sure you want to delete this project: <b>${projectSummary.projectNumber} - ${projectSummary.projectTitle}</b>?`,
      confirmButton: {
        label: 'Delete',
        color: 'error',
      },
      cancelButton: {
        label: 'Cancel',
        color: 'secondary',
      },
    });
    if (!confirmed) return;
    try {
      setDeleting(true);
      const response = await deleteProject(projectSummary.projectNumber);
      toast.success(response.message || response);
      navigate(ROUTES.PROJECTS);
    } catch (error) {
      handleError(error);
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!projectId || !projectSummary) {
      toast.error('Invalid project');
      return;
    }
    setGenerateReportInProgress(true);
    try {
      const reportName = `Cost Analysis Report: ${projectSummary.projectTitle} (${projectSummary.projectNumber})`;
      const reportGeneralInfo = {
        reportName: reportName,
      };
      await generateCAReport(projectId, reportGeneralInfo);
    } catch (error) {
      handleError(error);
    } finally {
      setGenerateReportInProgress(false);
    }
  };
  const generateCAReport = async (projectId: string, updatedGeneralInfo: ReportGeneralInfo) => {
    if (updatedGeneralInfo && !isValidGeneralInfo(updatedGeneralInfo)) {
      toast.error('Invalid report details. Please ensure all required fields are correctly filled.');
      return;
    }
    try {
      const report = await generateReport(projectId, updatedGeneralInfo);
      navigate(`${ROUTES.CA_REPORTS}/${report.id}`, { state: { reportId: report.id } });
      toast.success('Report created successfully');
    } catch (error) {
      handleError(error);
    } finally {
      setGenerateReportInProgress(false);
    }
  };

  const exportProject = async () => {
    setIsExporting(true);
    try {
      const blob = await exportProjectToExcel(projectId);

      if (!blob || blob.size === 0) {
        toast.error('Exported file is empty.');
        console.error('Error: Exported file is empty.');
        return;
      }

      const url = window.URL.createObjectURL(blob);

      // Determine locale-based date format
      const locale = navigator.language || 'en-US';
      const currentDate = new Date();
      const formattedDate = new Intl.DateTimeFormat(locale, {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
      })
        .format(currentDate)
        .replace(/\//g, '-');

      // Trigger file download
      const link = document.createElement('a');
      link.href = url;
      link.download = `${project.projectSummary.projectNumber} ${project.projectSummary.projectTitle}_${formattedDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Project exported successfully.');

      // Cleanup
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error exporting project.');
    }
    setIsExporting(false);
  };

  const handleEditDrawSubmit = async (vals: CreateOrUpdateProjectDrawDTO) => {
    setEditDrawInProgress(true);
    try {
      const isNewDraw = !vals.drawId;

      // Store the current draw ID before updating
      const currentDrawId = selectedDrawKey;

      // Validate draw uniqueness
      const validation = validateDrawUniqueness(detailedDraws, vals.name || '', vals.applicationNumber || '', vals.drawId);

      if (!validation.isValid) {
        toast.error(validation.errorMessage);
        return;
      }

      const drawId = vals.drawId || `temp-${Date.now()}`;

      const existingDraw = detailedDraws.find(draw => draw.projectDrawSummary.id === drawId);
      const existingBudget = existingDraw?.budgetSummary || {
        originalContractBudgetSum: 0,
        reallocationsChangeOrders: 0,
        contractBudgetSumToDate: 0,
        totalCompletedAndStored: 0,
        totalRetainageHeld: 0,
        totalRetainageHeldPercentage: 0,
        totalEarnedLessRetainage: 0,
        lessPreviousFundings: 0,
        currentPaymentDue: 0,
        balanceToCompletePlusRetainage: 0,
        adjustmentsByCDCC: 0,
        totalAmountReccForFunding: 0,
        requestedAmount: 0,
        difference: 0,
        estRetainageReleasedThisPeriod: 0,
        totalContractBudgetPerComplete: 0,
      };

      const updatedBudget = { ...existingBudget, ...vals.drawBudget };

      const updatedDraw: ProjectLineItemsAndSummary = {
        projectDrawSummary: {
          id: drawId,
          name: vals.name || 'Untitled Draw',
          applicationNumber: vals.applicationNumber || '',
          periodBeginning: vals.periodBeginning || '',
          periodEnding: vals.periodEnding || '',
          inputRetainagePercentage: vals.inputRetainagePercentage || 1,
        },
        lineItems: existingDraw?.lineItems || [],
        budgetSummary: updatedBudget,
        ...(isNewDraw
          ? {
              isCreated: true,
              isEdited: false,
              isDeleted: false,
            }
          : {
              isDeleted: false,
              isCreated: existingDraw?.isCreated ? true : false,
              isEdited: !existingDraw?.isCreated,
            }),
      };

      // Update local state (detailedDraws)
      const updatedDetailedDraws = detailedDraws?.some(draw => draw.projectDrawSummary.id === drawId)
        ? detailedDraws?.map(draw => (draw.projectDrawSummary.id === drawId ? updatedDraw : draw))
        : [...detailedDraws, updatedDraw];

      setDetailedDraws(updatedDetailedDraws);
      setSelectedDrawId(isNewDraw ? drawId : currentDrawId);

      // Update global state (projectDraws in project state)
      const updatedProjectDraws = project.projectDraws.some(draw => draw.projectDrawSummary.id === drawId)
        ? project.projectDraws.map(draw => (draw.projectDrawSummary.id === drawId ? updatedDraw : draw))
        : [...project.projectDraws, updatedDraw];

      updateProject({ projectDraws: updatedProjectDraws }, true);

      setEditDrawDialogOpen(false);
    } catch (error) {
      handleError(error);
    } finally {
      setEditDrawInProgress(false);
    }
  };

  const handleCreateDrawClick = () => {
    setEditDrawDialogValues({ projectId: projectId, drawBudget: {} });
    setEditDrawDialogOpen(true);
  };

  const handleUpdateDrawClick = () => {
    if (isProjectDirty) {
      toast.error('Please save existing changes before editing a G702/3.');
      return;
    }
    setIsEditMode(true);
  };

  const handleDeleteDrawClick = async () => {
    const nonDeletedDraws = filteredDraws;
    if (!selectedDrawKey) {
      toast.error('Could not find the G702/3 to delete!');
      return;
    }
    try {
      setDeleteDrawInProgress(true);

      // Check if this is the last budget
      const isLastBudget = nonDeletedDraws.length === 1;

      const isConfirmed = await getConfirmation({
        heading: 'Delete G702/3',
        description: isLastBudget
          ? `Deleting the last G702/3 will leave this project without any line items.<br> This may impact reports where the project is used as a comparable.<br><br>Are you sure you want to proceed with deleting this G702/3?`
          : `Are you sure you want to delete the G702/3 '${selectedDraw?.projectDrawSummary.name}'?`,
        confirmButton: {
          label: 'Delete',
          color: 'error',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });
      if (!isConfirmed) return;

      const drawToDeleteId = selectedDrawKey;

      // Set isDeleted to true for the selected draw in detailedDraws
      const updatedDetailedDraws = detailedDraws.map(draw =>
        draw.projectDrawSummary.id === drawToDeleteId ? { ...draw, isDeleted: true, isEdited: false, isCreated: false } : draw,
      );
      setDetailedDraws(updatedDetailedDraws);

      // Select the neighbouring tab
      const deletedIndex = filteredDraws.findIndex(draw => draw.projectDrawSummary.id === drawToDeleteId);
      const nextDrawId = filteredDraws[deletedIndex > 0 ? deletedIndex - 1 : deletedIndex + 1]?.projectDrawSummary.id;

      // Set isDeleted to true for the selected draw in projectDraws
      const updatedProjectDraws = project.projectDraws.map(draw =>
        draw.projectDrawSummary.id === drawToDeleteId ? { ...draw, isDeleted: true, isEdited: false, isCreated: false } : draw,
      );
      updateProject({ projectDraws: updatedProjectDraws }, true);

      setSelectedDrawId(nextDrawId);
    } catch (error) {
      handleError(error);
    } finally {
      setDeleteDrawInProgress(false);
    }
  };

  // Number of visible draws — the project must always keep at least one, so the last visible
  // draw cannot be hidden (the backend enforces this too).
  const visibleDrawCount = React.useMemo(
    () => filteredDraws.filter(draw => draw.projectDrawSummary.isEnabled !== false).length,
    [filteredDraws],
  );

  const applyDrawVisibility = (drawIds: string[], isEnabled: boolean) => {
    const applyVisibility = (draws: ProjectLineItemsAndSummary[]) =>
      draws.map(d =>
        drawIds.includes(d.projectDrawSummary.id) ? { ...d, projectDrawSummary: { ...d.projectDrawSummary, isEnabled } } : d,
      );

    setDetailedDraws(prev => applyVisibility(prev));
    updateProject({ projectDraws: applyVisibility(project.projectDraws) }, false, true);
    // Patch the cached list rather than refetching it: a refetch rebuilds every draw from the list
    // endpoint, wiping already-loaded details and making tabs reload and flicker.
    queryClient.setQueryData<DrawSummary[] | undefined>([keys.projectDraws, projectId], prev =>
      prev?.map(d => (drawIds.includes(d.id) ? { ...d, isEnabled } : d)),
    );
  };

  const handleToggleDrawVisibility = async () => {
    const draw = selectedDraw;
    const drawId = draw?.projectDrawSummary.id;
    if (!drawId || drawId.startsWith('temp-')) {
      toast.error('Please save this G702/3 before hiding or unhiding it.');
      return;
    }
    if (isProjectDirty) {
      toast.error('Please save existing changes before hiding or unhiding a G702/3.');
      return;
    }

    const nextIsEnabled = draw.projectDrawSummary.isEnabled === false;
    if (!nextIsEnabled && visibleDrawCount <= 1) {
      toast.error('At least one G702/3 must stay visible on the project.');
      return;
    }

    try {
      setVisibilityChangeInProgress(true);
      await setProjectDrawVisibility(projectId ?? '', drawId, nextIsEnabled);
      applyDrawVisibility([drawId], nextIsEnabled);
      toast.success(nextIsEnabled ? 'G702/3 is now visible.' : 'G702/3 is now hidden.');
    } catch (error) {
      handleError(error);
    } finally {
      setVisibilityChangeInProgress(false);
    }
  };

  const drawActions = (
    <Stack direction='row' spacing={1} pb={1} justifyContent='flex-end'>
      <Tooltip title='Update G702/3' arrow={false}>
        <div>
          <WhiteFab
            size='small'
            icon={<Edit fontSize='small' />}
            onClick={handleUpdateDrawClick}
            textColor='primary'
            disabled={!currentDraw || isEditMode || isProjectDirty}
          />
        </div>
      </Tooltip>
      <Tooltip title={visibleDrawCount <= 1 ? 'At least one G702/3 must stay visible' : 'Hide G702/3'} arrow={false}>
        <div>
          <WhiteFab
            size='small'
            icon={<VisibilityOff fontSize='small' />}
            onClick={handleToggleDrawVisibility}
            textColor='primary'
            disabled={!selectedDrawKey || visibleDrawCount <= 1 || isEditMode || isProjectDirty || visibilityChangeInProgress}
          />
        </div>
      </Tooltip>
      <Tooltip title='Delete G702/3' arrow={false}>
        <div>
          <WhiteFab
            size='small'
            icon={<Delete fontSize='small' />}
            onClick={handleDeleteDrawClick}
            textColor='error'
            disabled={!selectedDrawKey}
          />
        </div>
      </Tooltip>
    </Stack>
  );

  const handleSave = async () => {
    try {
      await onSave();
      setShouldRefetchNotes(prev => !prev); // Toggle to trigger refetch
    } catch (error) {
      handleError(error);
    }
  };

  // Auto-save after payapp data has been merged into the project state. Passing the draw id lets the
  // save record the upload against the magic report's current period.
  React.useEffect(() => {
    if (hasCalledHandlePayappSave && payappMergedDraw) {
      (async () => {
        try {
          await onSave(payappDrawId);
          setShouldRefetchNotes(prev => !prev);
        } catch (error) {
          handleError(error);
        }
      })().finally(() => setIsPayappAutoSaving(false));
    }
  }, [hasCalledHandlePayappSave]);

  return (
    <>
      {isPayappAutoSaving && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#ffffff',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src='/cdata.gif' alt='loading...' style={{ width: 150, height: 'auto' }} />
        </div>
      )}
      <ProjectSummary
        project={projectSummary}
        isLoading={isLoadingProjectSummary}
        isError={isErrorProjectSummary}
        error={errorProjectSummary}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        onEditChange={onEditChange}
        projectErrors={projectErrors}
        readonly={readonly}
      />
      <br />

      {currentDraw && (
        <>
          {/* The tab bar stays mounted while a draw loads, so tabs never flash or jump. */}
          <Box position='relative'>
            <Tabs
              value={selectedDrawKey ?? false}
              onChange={handleTabChange}
              sx={{
                '.MuiTab-root': {
                  '&:hover': {
                    backgroundColor: '#eaf5f7',
                  },
                  fontWeight: 'bold',
                  transition: 'opacity 200ms ease',
                },
              }}
            >
              {filteredDraws.map(draw => {
                const drawEnabled = draw.projectDrawSummary.isEnabled !== false;
                return (
                  <Tab
                    key={draw.projectDrawSummary.id}
                    value={draw.projectDrawSummary.id}
                    sx={{ opacity: drawEnabled ? 1 : 0.7 }}
                    label={
                      <Box display='flex' alignItems='center'>
                        {draw.projectDrawSummary.name}
                        {/* Width animates open/closed so neighbouring tabs glide instead of jumping. */}
                        <Collapse in={!drawEnabled} orientation='horizontal' timeout={200}>
                          <Chip label='Hidden' size='small' color='default' sx={{ ml: 1, fontSize: '0.65rem', height: 18 }} />
                        </Collapse>
                      </Box>
                    }
                  />
                );
              })}
            </Tabs>
            <Fade in={visibilityChangeInProgress} unmountOnExit>
              <LinearProgress sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2 }} />
            </Fade>
          </Box>
          {isLoadingCurrentDraw ? (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 6 }}>
                <Skeleton variant='rounded' width={'100%'} height={120} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Skeleton variant='rounded' width={'100%'} height={120} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Skeleton variant='rounded' width={'100%'} height={240} />
              </Grid>
            </Grid>
          ) : (
            filteredDraws.map(draw => {
              const drawEnabled =
                draw.projectDrawSummary.isEnabled !== false || (isPayappAutoSaving && draw.projectDrawSummary.id === payappDrawId);
              return (
                <TabPanel key={draw.projectDrawSummary.id} value={selectedDrawKey} index={draw.projectDrawSummary.id}>
                  {selectedDrawKey === draw.projectDrawSummary.id && (
                    <Fade in key={drawEnabled ? 'visible' : 'hidden'} timeout={250}>
                      <div>
                        {drawEnabled ? (
                          <>
                            <ProjectDrawSummary
                              drawSummary={currentDraw.projectDrawSummary}
                              headerAction={drawActions}
                              errorFieldNames={getErrorFieldNames(projectErrors, draw.projectDrawSummary.name)}
                              readonly={readonly}
                              isEditMode={isEditMode}
                              onEditChange={handleDrawEditChange}
                              assignedLineItemsCount={assignedDrawLineItemsCount}
                              totalLineItemsCount={totalDrawLineItemsCount}
                            />
                            {/* Mirrors the Excel G702/3 sheet: previous fundings on the left, single-column budget summary on the right. */}
                            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                              <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
                                <ProjectPreviousFundingsTable
                                  fullHeight
                                  previousFundings={currentDraw.projectDrawSummary.previousFundings}
                                  isEditMode={isEditMode}
                                  readonly={readonly}
                                  onChange={updated => {
                                    const previousTotal = sumPreviousFundings(
                                      currentDraw.projectDrawSummary.previousFundings,
                                      'amountFunded',
                                    );
                                    const updatedTotal = sumPreviousFundings(updated, 'amountFunded');
                                    const edits: Array<{ field: string; value: any; target: 'drawSummary' | 'budgetSummary' }> = [
                                      { field: 'previousFundings', value: updated, target: 'drawSummary' },
                                    ];
                                    if (Math.abs(previousTotal - updatedTotal) >= 0.01) {
                                      edits.push({ field: 'lessPreviousFundings', value: updatedTotal, target: 'budgetSummary' });
                                    }
                                    handleDrawEditChange(edits);
                                  }}
                                />
                              </Grid>
                              <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
                                <ProjectBudget
                                  singleColumn
                                  fullHeight
                                  budgetSummary={currentBudgetSummary}
                                  drawBudget={currentDraw.budgetSummary}
                                  errorFieldNames={getErrorFieldNames(projectErrors, draw.projectDrawSummary.name)}
                                  isEditMode={isEditMode}
                                  onEditChange={handleDrawEditChange}
                                  previousFundingsTotal={previousFundingsTotal}
                                />
                              </Grid>
                            </Grid>
                            <ProjectTable
                              scrollHeight={PROJECT_TABLE_SCROLL_HEIGHT}
                              projectId={projectId}
                              projectNumber={projectSummary?.projectNumber}
                              projectName={projectSummary?.projectTitle}
                              projectDrawId={draw.projectDrawSummary.id}
                              projectDrawName={draw.projectDrawSummary.name}
                              drawInputRetainagePercentage={(draw?.projectDrawSummary?.inputRetainagePercentage || 0) / 100}
                              onDrawInputRetainagePercentageChange={handleDrawEditChange}
                              inputRetainagePercentageHasError={getErrorFieldNames(projectErrors, draw.projectDrawSummary.name).includes(
                                'Input Retainage Percentage',
                              )}
                              lineItems={currentDraw.lineItems || []}
                              payappDraftId={currentDraw.payappId}
                              openAutoAssignDialog={openAutoAssignDialog}
                              predictingDivisions={predictingDivisions}
                              divisionsData={divisions || []}
                              isLoading={isLoading}
                              updateProject={updateProject}
                              detailedDraws={filteredDraws}
                              setDetailedDraws={setDetailedDraws}
                              readonly={readonly}
                              payappDrawId={payappDrawId}
                              payappMergedDraw={payappMergedDraw}
                              hasCalledHandlePayappSave={hasCalledHandlePayappSave}
                              setHasCalledHandlePayappSave={setHasCalledHandlePayappSave}
                              handleEditDrawSubmit={handleEditDrawSubmit}
                              isProjectDirty={isProjectDirty}
                              onOpenExistingLimitedReport={
                                currentBudgetSummary ? () => handleOpenExistingLimitedReport(currentDraw, currentBudgetSummary) : undefined
                              }
                              onOpenExistingMagicReport={currentBudgetSummary ? () => handleOpenExistingMagicReport() : undefined}
                              onCreateNewMagicReport={currentBudgetSummary ? () => handleCreateMagicReportForDraw() : undefined}
                              onCreateNewLimitedReport={() => handleCreateNewLimitedReportForDraw(currentDraw, currentBudgetSummary)}
                              setIsEditMode={setIsEditMode}
                              monitoringType={projectSummary?.monitoringType}
                              limitedReportId={projectSummary?.limitedReportId}
                              magicReportId={projectSummary?.magicReportId}
                              magicReportAwaitingPayapp={magicReportAwaitingPayapp}
                            />
                          </>
                        ) : (
                          <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant='h5' color='text.secondary' gutterBottom>
                              {draw.projectDrawSummary.name}
                            </Typography>
                            <Chip label='Hidden' color='default' />
                            <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
                              This draw is hidden and not available for editing or reporting. Its line items, budget and previous fundings
                              are all still stored — unhide it to work with them again.
                            </Typography>
                            {!readonly && (
                              <Button
                                variant='contained'
                                startIcon={<Visibility fontSize='small' />}
                                onClick={handleToggleDrawVisibility}
                                disabled={visibilityChangeInProgress}
                                sx={{ mt: 2 }}
                              >
                                Unhide G702/3
                              </Button>
                            )}
                          </Box>
                        )}
                      </div>
                    </Fade>
                  )}
                </TabPanel>
              );
            })
          )}
        </>
      )}
      {!readonly && (
        <>
          {filteredDraws.length === 0 ? (
            <Stack direction='row' justifyContent='center'>
              <Button variant='contained' onClick={handleCreateDrawClick}>
                Add New G702/3
              </Button>
            </Stack>
          ) : null}
          <Accordion
            elevation={3}
            style={{ marginTop: '50px', borderRadius: '8px' }}
            sx={{
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}
            >
              <Box display='flex' alignItems='center'>
                <Assessment color='primary' sx={{ fontSize: '24px !important', fontWeight: '700 !important' }} />
                <Typography variant='h5' color='primary' sx={{ ml: 1, mb: 0 }}>
                  Linked Reports ({associatedReports ? associatedReports.length : null})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {isLoadingReports ? (
                <LinearProgress />
              ) : isErrorReports ? (
                <Typography variant='button' fontWeight='medium' gutterBottom>
                  Error loading Linked Reports
                </Typography>
              ) : associatedReports && associatedReports.length > 0 ? (
                associatedReports.map(report => (
                  <Box key={report.id} sx={{ paddingY: 1 }}>
                    <Link
                      style={{ textDecoration: 'none', color: 'unset', cursor: 'pointer' }}
                      to={`${ROUTES.CA_REPORTS}/${report.id}`}
                      target='_blank'
                    >
                      <Typography variant='button' fontWeight='medium' gutterBottom>
                        {report.reportName} <Launch sx={{ fontSize: '16px', ml: '5px' }} color='secondary' />
                      </Typography>
                    </Link>
                  </Box>
                ))
              ) : (
                <Typography variant='button' fontWeight='medium' gutterBottom>
                  No Reports
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
          <ProjectMiscTabs projectId={projectId} notes={notes} setNotes={setNotes} isSaving={isSaving} shouldRefetch={shouldRefetchNotes} />
          <br></br>
          {projectSummary?.projectAddress && projectSummary?.projectAddress.latitude && projectSummary?.projectAddress.longitude ? (
            <ProjectMap
              key={`${projectSummary.projectAddress.latitude}-${projectSummary.projectAddress.longitude}`}
              lat={projectSummary.projectAddress.latitude}
              lng={projectSummary.projectAddress.longitude}
            />
          ) : (
            <p style={{ marginLeft: '20px', color: '#555', fontSize: '16px', fontStyle: 'italic' }}>
              Map not displayed. Coordinates are missing for this project.
            </p>
          )}

          <br></br>
          {projectSummary ? (
            <ProjectFooter
              projectId={projectId}
              text={`${joinDateTimeAndUser(projectSummary.updatedOn, projectSummary.updatedBy, 'Last Modified on ')}`}
              deleting={deleting}
              onDelete={onDelete}
              isGeneratingReport={generateReportInProgress}
              onGenerateReport={handleGenerateReport}
              isSaving={isSaving}
              isEditMode={isEditMode}
              onSave={handleSave}
              onCancel={onCancel}
              isProjectDirty={isProjectDirty}
              exportProject={exportProject}
              isExporting={isExporting}
              onCreateNewLimitedReport={() => handleCreateNewLimitedReportForDraw(currentDraw, currentBudgetSummary)}
              onOpenExistingMagicReport={currentBudgetSummary && currentDrawIsEnabled ? () => handleOpenExistingMagicReport() : undefined}
              onCreateNewMagicReport={
                currentBudgetSummary && currentDraw && currentDrawIsEnabled
                  ? () => {
                      handleCreateMagicReportForDraw();
                    }
                  : undefined
              }
              onOpenExistingLimitedReport={() => handleOpenExistingLimitedReport(currentDraw, currentBudgetSummary)}
              monitoringType={projectSummary?.monitoringType}
              limitedReportId={projectSummary?.limitedReportId}
              magicReportId={projectSummary?.magicReportId}
              magicReportAwaitingPayapp={magicReportAwaitingPayapp}
              onNextMagicReportStarted={handleNextMagicReportStarted}
              onAddDraw={handleCreateDrawClick}
              onMagicReportDrawsHidden={handleMagicReportDrawsHidden}
            />
          ) : null}
          <AutoAssignDivisionsDialog
            open={isAutoAssignDialogOpen}
            onClose={() => setIsAutoAssignDialogOpen(false)}
            lineItems={lineItemsToAutoAssign}
            updateDivisions={updateDivisions}
            divisions={divisions || []}
            showToggle={true}
          />
          <MagicReportDrawSelectDialog
            open={magicReportDrawSelectOpen}
            onClose={closeMagicReportDrawSelect}
            draws={magicReportDraws}
            projectName={magicReportProject?.projectName}
            onConfirm={handleMagicReportDrawConfirm}
            isLoading={magicReportOpening}
            initiallySelectedDrawIds={magicReportExistingDrawIds}
            initiallyHiddenCoLienDrawIds={magicReportExistingHiddenCoLienDrawIds}
            hasExistingReport={hasExistingMagicReport}
            mode={magicReportMode}
          />
          {requiredFieldsGapProjectId && requiredFieldsGapProjectSummary && (
            <RequiredFieldsGateDialog
              open={requiredFieldsGapOpen}
              projectId={requiredFieldsGapProjectId}
              missingFields={requiredFieldsGap}
              projectSummary={requiredFieldsGapProjectSummary}
              onSaved={retryAfterFieldsFixed}
              onCancel={closeRequiredFieldsGap}
            />
          )}
          <EditProjectDrawDialog
            open={editDrawDialogOpen}
            onCancel={() => setEditDrawDialogOpen(false)}
            initialValues={editDrawDialogValues}
            setValues={handleEditDrawSubmit}
            loading={editDrawInProgress}
            errorFieldNames={getErrorFieldNames(projectErrors, editDrawDialogValues?.name)}
            projectNumber={projectSummary?.projectNumber}
            projectName={projectSummary?.projectTitle}
            divisions={divisions || []}
            setDetailedDraws={setDetailedDraws}
            existingDraws={detailedDraws}
          />
        </>
      )}
    </>
  );
};

export default ProjectDetails;
